import type { Dispatch } from 'redux';
import {
  OWNER_SETUP_FEATURE_VERSION,
  markOwnerSetupEligible,
  markOwnerSetupSessionBootstrapError,
  markOwnerSetupSessionReady,
  resetOwnerActivationForIdentity,
  type OwnerActivationState,
} from '../../redux/ownerActivationSlice';
import type { OwnerSetupSessionHandoffMarker } from '../../services/ownerSetupSessionHandoff';

/**
 * Cold-start recovery for an interrupted owner-setup handoff (US1, T033).
 *
 * Covers every process-death window of `completeOwnerSetupHandoff`:
 *  - died before the eligibility flush → reconstruct pending eligibility from
 *    the transient handoff marker;
 *  - died after the flush → persisted pending eligibility drives recovery;
 *  - fresh tokens invalid → `requires_login`, eligibility preserved;
 *  - profile owner differs from the bound owner → reset activation residue and
 *    continue as a normal session;
 *  - network/profile failure → recoverable error, caller offers Retry.
 */

export type OwnerSetupRecoveryOutcome =
  /** Eligibility pending state absent: nothing to recover. */
  | { kind: 'not_needed' }
  /** Recovery finished; wizard entry decision will now fire. */
  | { kind: 'ready' }
  /** Tokens invalid/absent: Login must run; eligibility preserved. */
  | { kind: 'requires_login' }
  /** Profile belongs to a different owner: activation reset, session valid. */
  | { kind: 'reset_and_continue' }
  /** Transient failure; caller renders Retry / Sign in again. */
  | { kind: 'recoverable_error'; error: 'network' | 'profile_unavailable' };

export interface OwnerSetupRecoverySnapshot {
  ownerKey: OwnerActivationState['ownerKey'];
  eligibility: OwnerActivationState['eligibility'];
}

export interface OwnerSetupRecoveryDeps {
  dispatch: Dispatch;
  /** Live activation snapshot (call again after dispatches). */
  getActivationSnapshot(): OwnerSetupRecoverySnapshot;
  readHandoffMarker(): Promise<OwnerSetupSessionHandoffMarker | null>;
  clearHandoffMarker(): Promise<void>;
  /** Both tokens from AsyncStorage, or null when either is missing. */
  readStoredTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | null>;
  /** Remove invalid tokens so Login starts clean. */
  clearStoredTokens(): Promise<void>;
  flushPersistor(): Promise<unknown>;
  fetchEmployeeProfile(accessToken: string): Promise<any>;
  /** True when the fetch failure is an auth rejection (401/403). */
  isAuthRejection(error: unknown): boolean;
  /** Apply employee + business info to Redux; returns employee id. */
  applyEmployeeProfile(profileResponse: any): string;
  /**
   * Login-parity preload (employee locations + owner backoffice); failures
   * are tolerated. `ownerId` is the authenticated owner key for owner preload.
   */
  preloadSession(
    accessToken: string,
    employeeId: string,
    ownerId: string,
  ): Promise<void>;
  setAuthTokens(accessToken: string, refreshToken: string): void;
  now(): string;
}

export async function runOwnerSetupSessionRecovery(
  deps: OwnerSetupRecoveryDeps,
): Promise<OwnerSetupRecoveryOutcome> {
  let marker = await deps.readHandoffMarker();
  const initial = deps.getActivationSnapshot();

  if (marker) {
    const boundToOtherOwner =
      initial.eligibility.eligible &&
      initial.ownerKey !== null &&
      initial.ownerKey !== marker.ownerKey;
    if (boundToOtherOwner) {
      // Stale marker from a different owner's interrupted signup.
      await deps.clearHandoffMarker();
      marker = null;
    } else if (!initial.eligibility.eligible) {
      // Process died between token batch-write and eligibility flush:
      // reconstruct the pending envelope from the marker.
      deps.dispatch(
        markOwnerSetupEligible({
          ownerKey: marker.ownerKey,
          markedAt: deps.now(),
          source: 'business_setup_completion',
          featureVersion: OWNER_SETUP_FEATURE_VERSION,
          sessionBootstrap: 'pending',
        }),
      );
      await deps.flushPersistor();
    }
  }

  const snapshot = deps.getActivationSnapshot();
  if (
    !snapshot.eligibility.eligible ||
    snapshot.eligibility.sessionBootstrap !== 'pending'
  ) {
    if (marker) {
      // Marker outlived a completed/never-started handoff: drop it.
      await deps.clearHandoffMarker();
    }
    return { kind: 'not_needed' };
  }

  const tokens = await deps.readStoredTokens();
  if (!tokens) {
    deps.dispatch(
      markOwnerSetupSessionBootstrapError({ error: 'invalid_session' }),
    );
    await deps.flushPersistor();
    await deps.clearHandoffMarker();
    return { kind: 'requires_login' };
  }

  let profileResponse: any;
  try {
    profileResponse = await deps.fetchEmployeeProfile(tokens.accessToken);
  } catch (error) {
    if (deps.isAuthRejection(error)) {
      return invalidateSession(deps);
    }
    deps.dispatch(markOwnerSetupSessionBootstrapError({ error: 'network' }));
    return { kind: 'recoverable_error', error: 'network' };
  }

  if (
    profileResponse?.data?.data?.bp_employee_requires_reauthentication ||
    profileResponse?.data?.message === 'Employee does not exist'
  ) {
    return invalidateSession(deps);
  }
  if (profileResponse?.data?.status !== 200) {
    deps.dispatch(
      markOwnerSetupSessionBootstrapError({
        error: 'employee_profile_unavailable',
      }),
    );
    return { kind: 'recoverable_error', error: 'profile_unavailable' };
  }

  const profileOwnerKey = String(
    profileResponse?.data?.data?.bp_owner_id || '',
  );

  if (!profileOwnerKey || profileOwnerKey !== snapshot.ownerKey) {
    // Valid session, but for a different owner than the bound envelope:
    // reset activation residue and continue as a normal session.
    deps.dispatch(
      resetOwnerActivationForIdentity({ ownerKey: profileOwnerKey || null }),
    );
    await deps.clearHandoffMarker();
    const employeeId = deps.applyEmployeeProfile(profileResponse);
    try {
      await deps.preloadSession(
        tokens.accessToken,
        employeeId,
        profileOwnerKey,
      );
    } catch (error) {
      console.log('owner setup recovery preload skipped', error);
    }
    deps.setAuthTokens(tokens.accessToken, tokens.refreshToken);
    return { kind: 'reset_and_continue' };
  }

  const employeeId = deps.applyEmployeeProfile(profileResponse);
  try {
    await deps.preloadSession(
      tokens.accessToken,
      employeeId,
      profileOwnerKey,
    );
  } catch (error) {
    // Failure contract: session may become ready; location-dependent actions
    // surface their own Retry inside the wizard.
    console.log('owner setup recovery preload skipped', error);
  }
  deps.setAuthTokens(tokens.accessToken, tokens.refreshToken);
  deps.dispatch(markOwnerSetupSessionReady());
  await deps.flushPersistor();
  await deps.clearHandoffMarker();
  return { kind: 'ready' };
}

async function invalidateSession(
  deps: OwnerSetupRecoveryDeps,
): Promise<OwnerSetupRecoveryOutcome> {
  deps.dispatch(
    markOwnerSetupSessionBootstrapError({ error: 'invalid_session' }),
  );
  await deps.flushPersistor();
  await deps.clearStoredTokens();
  await deps.clearHandoffMarker();
  return { kind: 'requires_login' };
}
